import { useMemo } from 'react';
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Brand60, Brand80, Neutral500 } from '../../../../utils/colors';
import { MediaPrior } from '../data-qa-types';
import {
  computePriorMean,
  computePriorPdf,
  formatPriorMean,
  formatPriorParams
} from '../prior-utils';

interface PriorCardProps {
  prior: MediaPrior;
}

const axisTickFormatter = (v: number) => Number(v.toPrecision(2)).toString();

export const PriorCard = ({ prior }: PriorCardProps) => {
  const { distribution, param1, param2, priorType, description } = prior;

  const pdf = useMemo(
    () => computePriorPdf(distribution, param1, param2),
    [distribution, param1, param2]
  );
  const mean = useMemo(
    () => computePriorMean(distribution, param1, param2),
    [distribution, param1, param2]
  );
  const paramsStr = formatPriorParams(distribution, param1, param2);
  const meanLabel = priorType ? `Mean ${priorType.toUpperCase()}` : 'Mean';
  const showMeanLine =
    mean !== null && pdf !== null && mean >= pdf[0].x && mean <= pdf[pdf.length - 1].x;

  return (
    <div className='data-qa-prior'>
      <div className='data-qa-prior__title'>{prior.title}</div>
      {prior.subtitle && <div className='data-qa-prior__subtitle'>{prior.subtitle}</div>}
      <div className='data-qa-prior__badges'>
        {description && (
          <span className='data-qa-prior__badge data-qa-prior__badge--desc'>{description}</span>
        )}
        <span className='data-qa-prior__badge data-qa-prior__badge--params'>{paramsStr}</span>
        {mean !== null && (
          <span className='data-qa-prior__badge data-qa-prior__badge--mean'>
            {meanLabel}: {formatPriorMean(mean)}
          </span>
        )}
        {priorType && (
          <span className='data-qa-prior__badge data-qa-prior__badge--type'>type: {priorType}</span>
        )}
      </div>

      {pdf ? (
        <div className='data-qa-prior__chart'>
          <ResponsiveContainer width='100%' height={190}>
            <AreaChart data={pdf} margin={{ top: 24, right: 12, bottom: 8, left: 12 }}>
              <XAxis
                dataKey='x'
                type='number'
                domain={['dataMin', 'dataMax']}
                tickFormatter={axisTickFormatter}
                tick={{ fontSize: 10, fill: Neutral500 }}
                tickLine={false}
                axisLine={{ stroke: Neutral500 }}
              />
              <YAxis hide domain={[0, 'dataMax']} />
              <Area
                type='monotone'
                dataKey='y'
                stroke={Brand60}
                strokeWidth={2}
                fill={Brand60}
                fillOpacity={0.15}
                isAnimationActive={false}
                dot={false}
              />
              {showMeanLine && mean !== null && (
                <ReferenceLine
                  x={mean}
                  stroke={Brand80}
                  strokeDasharray='3 3'
                  label={{
                    value: `mean ${formatPriorMean(mean)}`,
                    position: 'top',
                    fontSize: 10,
                    fill: Brand80
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className='data-qa-prior__unsupported'>
          Distribution <code>{distribution}</code> not supported for chart preview.
        </div>
      )}
    </div>
  );
};
